/**
 * The account control in the app bar.
 *
 * Signed out it is a sign-in button; signed in it is an avatar that opens a
 * small menu. It is the only marketing-adjacent thing allowed in the editor
 * chrome, and it stays one control wide.
 *
 * It never renders its own provider buttons — `/api/me` reports which providers
 * are configured, and a button for an unconfigured provider dead-ends, which
 * costs more trust than a missing one. Sign-in is the sheet's job.
 */

import { type Identity, onIdentityChange } from "../auth/session";
import { openSignInSheet } from "../auth/sign-in-sheet";
import { avatar } from "./avatar";
import { actionButton, el, replace } from "./dom";
import { tierChip } from "./tier-chip";

export interface AccountMenuOptions {
  /** Where "Your designs" points. */
  projectsHref?: string;
  /** Where "Settings" and "Billing" point. */
  settingsHref?: string;
  billingHref?: string;
  /** Called after the user confirms sign-out; the shell owns the redirect. */
  onSignOut?: () => Promise<void> | void;
}

export interface AccountMenu {
  el: HTMLElement;
  destroy(): void;
}

/** Deterministic, so a second menu on a page cannot collide with the first. */
let menuSeq = 0;

export function createAccountMenu(options: AccountMenuOptions = {}): AccountMenu {
  const {
    projectsHref = "/app/projects",
    settingsHref = "/app/settings",
    billingHref = "/app/settings/billing",
    onSignOut,
  } = options;

  const root = el("div", { class: "hud-account" });

  /**
   * A disclosure, not a menu.
   *
   * The trigger used to set `aria-haspopup="menu"`, which promises a screen
   * reader arrow-key navigation, Home/End and typeahead — none of which is
   * implemented. Mismatched ARIA is worse than none: it replaces working
   * default semantics with a contract the widget does not honour. A container
   * of links and a button toggled by `aria-expanded` is understood everywhere
   * and gets correct Tab and Enter behaviour for free.
   */
  const popoverId = `hud-account-menu-${++menuSeq}`;
  const popover = el("div", {
    class: "hud-account__menu",
    hidden: true,
    aria: { label: "Account" },
  });
  popover.id = popoverId;
  let open = false;

  /** The trigger, when one is rendered. Signed out there is only a button. */
  let trigger: HTMLButtonElement | null = null;

  function setOpen(next: boolean, restoreFocus = false) {
    open = next;
    popover.hidden = !next;
    root.dataset.open = String(next);
    // Updated here rather than only at render: aria-expanded was being written
    // once and then left, so every toggle after the first told a screen reader
    // the opposite of what was on screen.
    trigger?.setAttribute("aria-expanded", String(next));

    if (next) {
      // Somewhere to be. Opening a panel and leaving focus behind it means a
      // keyboard user has to Tab through it to reach anything.
      popover.querySelector<HTMLElement>(".hud-account__item")?.focus();
    } else if (restoreFocus) {
      // Escape and outside-click land differently: Escape is a deliberate
      // "back out", and focus has to return somewhere or it falls to the body.
      trigger?.focus();
    }
  }

  // Clicking anywhere else closes it. Registered once, not per render.
  const onDocumentClick = (e: Event) => {
    if (open && !root.contains(e.target as Node)) setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && open) setOpen(false, true);
  };
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onKey);

  function renderSignedOut(identity: Identity) {
    trigger = null;
    setOpen(false);
    replace(
      root,
      actionButton(
        "Sign in",
        async () => {
          // "none": signing in from the app bar resumes nothing in particular.
          await openSignInSheet({ reason: "Sign in", kind: "none", returnTo: location.pathname });
        },
        {
          class: "hud-account__signin",
          // Nothing to sign in *with* if no provider is configured. Better a
          // disabled control with a reason than one that opens an empty sheet.
          disabled: !identity.authConfigured,
          title: identity.authConfigured ? "" : "Sign-in is not configured on this site",
        },
      ),
    );
  }

  const error = el("p", {
    class: "hud-account__error",
    hidden: true,
    aria: { live: "polite" },
  });

  function renderSignedIn(identity: Identity) {
    const user = identity.user;
    const label = user?.name || user?.email || identity.devUser || "Account";

    trigger = el(
      "button",
      {
        class: "hud-account__trigger",
        type: "button",
        aria: { expanded: open, controls: popoverId, label: `Account: ${label}` },
        on: {
          click: (e) => {
            e.stopPropagation();
            setOpen(!open);
          },
        },
      },
      avatar(
        { image: user?.image, id: user?.id ?? identity.devUser ?? label, label },
        "hud-account__avatar",
      ),
    );

    replace(
      popover,
      el(
        "div",
        { class: "hud-account__identity" },
        el("span", { class: "hud-account__name", text: label }),
        user?.email ? el("span", { class: "hud-account__email", text: user.email }) : null,
        el("span", { class: "hud-account__tier" }, tierChip(identity.tier)),
      ),
      el("hr", { class: "hud-account__rule" }),
      el("a", { class: "hud-account__item", href: projectsHref, text: "Your designs" }),
      el("a", { class: "hud-account__item", href: settingsHref, text: "Settings" }),
      el("a", { class: "hud-account__item", href: billingHref, text: "Plan and billing" }),
      el("hr", { class: "hud-account__rule" }),
      actionButton(
        "Sign out",
        async () => {
          error.hidden = true;
          try {
            await onSignOut?.();
            // Only after it worked. Closing first made a failed sign-out
            // indistinguishable from a successful one — the menu vanished, the
            // session did not, and the user walked away from a shared machine
            // believing they had signed out.
            setOpen(false);
          } catch {
            error.textContent = "Could not sign out. You are still signed in.";
            error.hidden = false;
          }
        },
        { class: "hud-account__item hud-account__item--button" },
      ),
      error,
    );

    replace(root, trigger, popover);
  }

  const stop = onIdentityChange((identity) => {
    if (identity.signedIn || identity.devUser) renderSignedIn(identity);
    else renderSignedOut(identity);
  });

  return {
    el: root,
    destroy() {
      stop();
      document.removeEventListener("click", onDocumentClick);
      document.removeEventListener("keydown", onKey);
      root.remove();
    },
  };
}
